import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { lookupBusinessForLogin } from '../utils/publicBusiness';
import { useAuthStore } from '../store/useAuthStore';
import { useBusinessStore } from '../store/useBusinessStore';
import { CheckCircle, Sparkles, ArrowRight, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDefaultFeatures, buildEstiloWithFeatures } from '../utils/businessConfigHelpers';
import { startInTestMode } from '../utils/botTestMode';
import { demoFallbackBusiness, normalizeBusiness } from '../utils/normalizeBusiness';
import { getStoredAdminEmails, DEMO_ACCOUNTS, findDemoMatch, isDemoId } from '../utils/adminConstants';

export default function Login() {
  const { setAuth } = useAuthStore();
  const { masterDb, setMasterDb, setActiveBusinessId } = useBusinessStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [isSignUp, setIsSignUp] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [showDemoSelector, setShowDemoSelector] = useState(false);
  
  const [signupData, setSignupData] = useState({
    name: '',
    businessName: '',
    phone: '',
    email: '',
    password: '',
    acceptsTerms: false
  });

  const showNotification = (msg, type) => {
    if (type === 'error') toast.error(msg);
    else toast.success(msg);
  };

  const handleGoogleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.hostname === 'localhost' ? `${window.location.origin}/v2/dashboard` : 'https://keyla.seryiads.com/v2/dashboard'
        }
      });
      if (error) throw error;
    } catch (err) {
      console.error("Error en Google Login:", err);
      showNotification("Error al iniciar sesión con Google. Intenta de nuevo.", "error");
    }
  };

  const handleQuickDemoLogin = (demo) => {
    setLoginEmail(demo.demoEmail);
    setLoginPassword(demo.demoPassword || 'demo');
    
    // Auto login
    setTimeout(() => {
      executeDemoLogin(demo);
    }, 100);
  };

  // Una demo entra sin sesión de Supabase, así que nadie carga su negocio: sin él el menú ve todos los módulos apagados.
  // Se lee el negocio completo (si la base lo permite) o se arma uno con los módulos de la demo, y se deja en el estado de la app.
  const hydrateDemoBusiness = async (demoMatch, known) => {
    let biz = null;
    try {
      const { data } = await supabase
        .from('businesses')
        .select('*, staff(id, business_id, name, specialty, available, comision_porcentaje), services(*), appointments(*), orders(*)')
        .or(`id.eq.${demoMatch.legacyIds?.[0] || demoMatch.id},slug.eq.${demoMatch.slug}`)
        .maybeSingle();
      biz = data;
    } catch (e) {
      console.warn('No se pudo leer la demo sin sesión:', e);
    }
    const business = normalizeBusiness(biz || known || demoFallbackBusiness(demoMatch));
    if (typeof setMasterDb === 'function') {
      setMasterDb((current) => [...(Array.isArray(current) ? current : []).filter((b) => String(b.id) !== String(business.id)), business]);
    }
    return business;
  };

  const executeDemoLogin = async (demoMatch) => {
    setIsLoggingIn(true);
    // Find in masterDb or query Supabase
    let matchedBiz = (masterDb || []).find(b => 
      String(b.id) === demoMatch.id || 
      demoMatch.legacyIds.includes(String(b.id)) ||
      (b.slug && b.slug.toLowerCase() === demoMatch.slug.toLowerCase())
    );

    if (!matchedBiz) {
      const { data } = await supabase
        .from('businesses')
        .select('*')
        .or(`id.eq.${demoMatch.id},slug.eq.${demoMatch.slug}`)
        .maybeSingle();
      matchedBiz = data;
    }

    const business = await hydrateDemoBusiness(demoMatch, matchedBiz);
    const finalId = String(business.id);
    const bizName = business.nombre_negocio || demoMatch.name;

    setAuth(true, demoMatch.demoEmail, 'client');
    setActiveBusinessId(finalId);
    showNotification(`¡Accediendo a ${bizName}!`, "success");
    setIsLoggingIn(false);
    navigate('/v2/dashboard', { replace: true });
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword.trim()) {
      showNotification("Por favor ingresa tu correo y contraseña.", "error");
      return;
    }
    setIsLoggingIn(true);
    const emailClean = loginEmail.trim().toLowerCase();

    // 1. Verificación de Administrador Master
    const currentAdminEmails = getStoredAdminEmails().map(e => e.toLowerCase().trim());
    if (currentAdminEmails.includes(emailClean)) {
      // El master entra con contraseña real (sesión de Supabase): sin sesión la base de datos no le deja ver ni cambiar nada,
      // y escribir solo un correo de administrador no puede bastar para ver la consola.
      const { error: adminError } = await supabase.auth.signInWithPassword({ email: emailClean, password: loginPassword });
      if (adminError) {
        showNotification("Credenciales inválidas. Si entras con tu cuenta de Google, usa «Continuar con Google».", "error");
        setIsLoggingIn(false);
        return;
      }
      setAuth(true, emailClean, 'master');
      showNotification("¡Bienvenido Administrador Master!", "success");
      setIsLoggingIn(false);
      navigate('/master/dashboard', { replace: true });
      return;
    }

    // 2. Verificación de Cuentas Demo por Alias o Correo Demo Inmutable
    const demoMatch = findDemoMatch(emailClean);
    if (demoMatch) {
      await executeDemoLogin(demoMatch);
      return;
    }

    // 3. Verificación de Comercio Real o Demo con Correo Personal Configurado
    let businessMatch = (masterDb || []).find(b => 
      (b.google_calendar_id && b.google_calendar_id.toLowerCase().trim() === emailClean) ||
      String(b.id) === emailClean
    );

    if (!businessMatch) {
      // Aún sin sesión: la tabla de negocios no es pública, solo se pregunta si el correo es de un negocio (id, nombre, activo).
      businessMatch = await lookupBusinessForLogin(supabase, emailClean);
    }

    if (businessMatch) {
      const isDemo = isDemoId(businessMatch.id);

      // Si es una cuenta demo que tiene un correo real configurado
      if (isDemo && (loginPassword.trim() === 'demo' || loginPassword.trim() === 'demo1234')) {
        const demoMatch = findDemoMatch(String(businessMatch.id));
        if (demoMatch) await hydrateDemoBusiness(demoMatch, businessMatch);
        setAuth(true, emailClean, 'client');
        setActiveBusinessId(String(businessMatch.id));
        showNotification(`¡Bienvenido a ${businessMatch.nombre_negocio}! (Modo Demo)`, "success");
        setIsLoggingIn(false);
        navigate('/v2/dashboard', { replace: true });
        return;
      }

      // Si es un cliente real (ej: Seryiads u otro negocio)
      const { error } = await supabase.auth.signInWithPassword({
        email: emailClean,
        password: loginPassword,
      });

      // Un negocio real solo entra con su contraseña de Supabase. Las claves de demostración solo valen para las cuentas demo
      // (arriba); si el negocio aún no tiene contraseña, el administrador se la crea en Administrar Clientes.
      if (error) {
        showNotification("Credenciales inválidas. Si es tu primer ingreso, pide a tu administrador que te cree la contraseña.", "error");
        setIsLoggingIn(false);
        return;
      }

      if (businessMatch.activo === false) {
        showNotification("Tu cuenta se encuentra suspendida. Por favor contacta al administrador.", "error");
        await supabase.auth.signOut();
        setIsLoggingIn(false);
        return;
      }
      
      setAuth(true, emailClean, 'client');
      setActiveBusinessId(String(businessMatch.id));
      showNotification(`¡Bienvenido a ${businessMatch.nombre_negocio}!`, "success");
      setIsLoggingIn(false);
      navigate('/v2/dashboard', { replace: true });
      return;
    }

    showNotification(`El correo "${emailClean}" no está vinculado a ningún negocio registrado.`, "error");
    setIsLoggingIn(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!signupData.acceptsTerms) {
      showNotification("Debes aceptar los Términos y Condiciones.", "error");
      return;
    }
    setIsSigningUp(true);
    
    // 1. Registrar en Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: signupData.email.trim(),
      password: signupData.password,
    });

    if (authError) {
      showNotification("Error en registro: " + authError.message, "error");
      setIsSigningUp(false);
      return;
    }

    // 2. Crear Negocio con estado Trial
    const businessId = Date.now().toString();
    const baseSlug = signupData.businessName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const randomSuffix = Math.floor(1000 + Math.random() * 9000);
    const finalSlug = `${baseSlug}${randomSuffix}`;
    const instanceName = `${finalSlug}inst`;
    
    const defaultFeatures = getDefaultFeatures('servicios', 'free');
    const initialEstilo = `Eres Keyla, la asistente IA de ${signupData.businessName}. Tu misión es brindar una atención cordial, responder dudas y agendar citas de forma eficiente.`;
    const finalEstilo = buildEstiloWithFeatures(initialEstilo, defaultFeatures);

    const newBiz = {
      id: businessId,
      nombre_negocio: signupData.businessName,
      slug: finalSlug,
      instance_name: instanceName,
      google_calendar_id: signupData.email.trim().toLowerCase(),
      id_whatsapp: signupData.phone.replace(/\D/g, ''),
      plan: 'free',
      subscription_status: 'trial',
      accepts_commercial_emails: true,
      activo: true,
      tipo_negocio: 'servicios',
      reglas_oro: `1. Eres el asistente IA de ${signupData.businessName}. Sé amable y profesional.`,
      estilo: finalEstilo,
      ...defaultFeatures
    };

    const { error: bizError } = await supabase.from('businesses').insert([newBiz]);

    if (bizError) {
      showNotification("Error creando el espacio de trabajo.", "error");
      console.error(bizError);
      setIsSigningUp(false);
      return;
    }

    // Un negocio nuevo arranca con el bot en modo prueba (responde solo al celular del dueño) hasta que lo encienda para todos.
    await startInTestMode(supabase, businessId, signupData.phone);

    showNotification("¡Cuenta creada con éxito! Ingresando...", "success");
    setIsSigningUp(false);
    
    if (authData.session) {
      setAuth(true, signupData.email.trim().toLowerCase(), 'client');
      setActiveBusinessId(businessId);
      navigate('/v2/dashboard', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-seryi-bg-dark flex flex-col justify-center items-center p-4 font-sans relative overflow-hidden">
      {/* Elementos decorativos de fondo */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-seryi-green/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/20 rounded-full blur-[120px]"></div>
      </div>

      <div className="bg-seryi-bg/80 backdrop-blur-xl border border-seryi-bg-light p-8 md:p-10 rounded-3xl shadow-2xl max-w-md w-full relative z-10 text-center space-y-6">
        <div className="flex flex-col items-center gap-3">
          <img src="/logo-icon.png" alt="Keyla CRM" className="w-16 h-16 drop-shadow-lg" />
          <div>
            <h1 className="font-black text-3xl text-seryi-heading tracking-tight">Keyla CRM</h1>
            <p className="text-xs text-seryi-green font-bold tracking-wider uppercase mt-1">Multi-Tenant</p>
          </div>
        </div>

        {location.search.includes('logout=true') && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-2xl text-xs font-semibold flex items-center justify-center gap-2 animate-fadeIn">
            <CheckCircle size={16} />
            <span>Has cerrado sesión correctamente</span>
          </div>
        )}
        
        {isSignUp ? (
          <form onSubmit={handleSignup} className="space-y-4 text-left pt-2">
            <div className="space-y-1">
              <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Tu Nombre</label>
              <input type="text" required className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                value={signupData.name} onChange={e => setSignupData({...signupData, name: e.target.value})} />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Nombre del Negocio</label>
              <input type="text" required className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                value={signupData.businessName} onChange={e => setSignupData({...signupData, businessName: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Teléfono</label>
                <input type="tel" required className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                  value={signupData.phone} onChange={e => setSignupData({...signupData, phone: e.target.value})} />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Email</label>
                <input type="email" required className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                  value={signupData.email} onChange={e => setSignupData({...signupData, email: e.target.value})} />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Contraseña</label>
              <input type="password" required minLength="6" className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                value={signupData.password} onChange={e => setSignupData({...signupData, password: e.target.value})} />
            </div>
            
            <div className="flex items-start gap-2 pt-2 pb-1">
              <input type="checkbox" required className="mt-1 bg-seryi-bg border border-seryi-bg-light text-seryi-green rounded"
                checked={signupData.acceptsTerms} onChange={e => setSignupData({...signupData, acceptsTerms: e.target.checked})} />
              <span className="text-[10px] text-seryi-text leading-tight">
                Al hacer clic en 'Crear Cuenta', aceptas nuestros Términos y Condiciones y autorizas a Seryiads/Keyla CRM al envío de comunicaciones comerciales y novedades vía correo electrónico.
              </span>
            </div>

            <button type="submit" disabled={isSigningUp} className="w-full bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-50 font-bold py-3 px-4 rounded-xl text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20">
              {isSigningUp ? "Creando Cuenta..." : "Crear Cuenta"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleEmailLogin} className="space-y-4 text-left pt-2">
            <div className="space-y-1">
              <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Correo o Identificador</label>
              <input 
                type="text" 
                required
                className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all font-medium"
                placeholder="ej: usuario@empresa.com o barberiademo@seryiads.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase font-bold text-seryi-text ml-1">Contraseña</label>
              <input 
                type="password" 
                required
                className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-indigo-500 transition-all"
                placeholder="••••••••"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
              />
            </div>

            <button 
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-seryi-green text-seryi-bg-dark hover:bg-seryi-green/80 disabled:opacity-50 font-bold py-3 px-4 rounded-xl text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-seryi-green/20 active:scale-98 cursor-pointer"
            >
              {isLoggingIn ? "Accediendo..." : "Iniciar Sesión"}
            </button>
          </form>
        )}

        <button 
          type="button" 
          onClick={() => setIsSignUp(!isSignUp)} 
          className="text-xs font-bold text-seryi-text hover:text-seryi-green transition-colors mt-2 block w-full"
        >
          {isSignUp ? "¿Ya tienes cuenta? Inicia sesión" : "¿No tienes cuenta? Regístrate"}
        </button>

        {/* Sección de Accesos Rápidos para Demos */}
        {!isSignUp && (
          <div className="pt-2 border-t border-seryi-bg-light/60">
            <button
              type="button"
              onClick={() => setShowDemoSelector(!showDemoSelector)}
              className="text-[11px] font-bold text-indigo-400 hover:text-indigo-300 flex items-center justify-center gap-1.5 w-full transition-colors py-1"
            >
              <Sparkles size={14} />
              <span>{showDemoSelector ? "Ocultar Cuentas Demo" : "⚡ Demostraciones en Vivo (1-Click)"}</span>
            </button>

            {showDemoSelector && (
              <div className="mt-3 space-y-2 text-left animate-fadeIn">
                <p className="text-[10px] text-seryi-text text-center mb-1">Selecciona una vertical para ingresar sin escribir contraseña:</p>
                <div className="grid grid-cols-1 gap-2">
                  {DEMO_ACCOUNTS.map((demo) => (
                    <button
                      key={demo.id}
                      type="button"
                      onClick={() => handleQuickDemoLogin(demo)}
                      className="bg-seryi-bg-dark/90 hover:bg-seryi-bg-light/60 border border-seryi-bg-light p-2.5 rounded-xl flex items-center justify-between transition-all group active:scale-98 text-left cursor-pointer"
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="text-base">{demo.icon}</span>
                        <div>
                          <p className="text-xs font-bold text-seryi-heading group-hover:text-seryi-green transition-colors">{demo.name}</p>
                          <p className="text-[10px] text-seryi-text">{demo.niche}</p>
                        </div>
                      </div>
                      <ArrowRight size={14} className="text-seryi-text group-hover:text-seryi-green group-hover:translate-x-0.5 transition-all" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Divisor */}
        <div className="relative flex items-center justify-center my-2">
          <div className="border-t border-seryi-bg-light w-full"></div>
          <span className="bg-seryi-bg px-3 text-[10px] uppercase font-bold text-seryi-text absolute">o</span>
        </div>

        {/* Botón Google OAuth */}
        <button 
          type="button"
          onClick={handleGoogleLogin}
          className="w-full bg-white hover:bg-slate-100 text-slate-900 font-bold py-3 px-4 rounded-xl text-sm transition-all flex items-center justify-center gap-3 shadow-lg cursor-pointer"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            <path fill="none" d="M1 1h22v22H1z" />
          </svg>
          Continuar con Google
        </button>
      </div>
    </div>
  );
}
